import {
    GA4EventName,
    type GA4ItemListName,
    type ProductType as TrackingProductType,
    trackEecToggleWishlistItem,
    trackToggleWishlist,
    trackWishlistError,
    type Variant,
    WishlistError,
    WishListEventType,
} from '@jsmdg/tracking';
import { useIsInWishlist, useWishlistActions, WishlistErrorTypes } from '@jsmdg/wishlist-library';
import { type WishlistResponseError } from '@jsmdg/wishlist-library/dist/esm/client/types';
import { NotificationVariant, useSnackbarContext } from '@jsmdg/yoshi';
import { ProductType } from '../../shared/enums/productType';
import { type Product } from '../../shared/types/searchResponse';
import { type WishlistData } from '../types/wishlist';

type UseWishListToggleRetrun = {
    isOnWishlist: boolean;

    onWishlistButtonClick: () => Promise<void>;
};

const buildEecWishlistPayload = (
    product: Product,
    itemListName: GA4ItemListName,
    hasLocationFilter?: boolean,
): Parameters<typeof trackEecToggleWishlistItem>[0] => ({
    product: {
        id: product.productId,
        url: product.url,
        trackingName: product.trackingName,
        manufacturerSku: product.manufacturerSku,
        trackingPath: product.trackingPath,
        price: {
            gross: {
                amount: Number.parseFloat(product.price.gross.amount),
                currencyCode: product.price.gross.currencyCode,
            },
        },
        originalPrice: product.originalPrice && {
            gross: {
                amount: Number.parseFloat(product.originalPrice.gross.amount),
                currencyCode: product.originalPrice.gross.currencyCode,
            },
        },
        title: product.title,
        rating: product.rating,
        participants: {
            min: product.participants?.min || 0,
            max: product.participants?.max || 0,
        },
        ...(product.locations ? { locationCount: product.locations.length } : {}),
        productType: product.productType as unknown as TrackingProductType,
        locationIsSelected: hasLocationFilter,
        discountBadgeId: product.discountBadge?.id,
        discountBadgeName: product.discountBadge?.text,
    },
    quantity: 1,
    variant: (product.productType === ProductType.ExperienceProduct
        ? 'experience_voucher'
        : product.productType) as Variant,
    eventName: GA4EventName.AddToWishlist,
    itemListName,
});

const isWishlistCountExceeded = (error: unknown): boolean =>
    (error as WishlistResponseError).response?.data?.message ===
        WishlistErrorTypes.COUNT_EXCEEDED ||
    (error as Error).message === WishlistErrorTypes.COUNT_EXCEEDED;

const useWishListToggle = (
    product: Product,
    messages: Record<string, string>,
    wishlistData: WishlistData,
    itemListName: GA4ItemListName,
    hasLocationFilter?: boolean,
): UseWishListToggleRetrun => {
    const { customerState = { isLoggedIn: false }, initialWishlist, errorLogger } = wishlistData;
    const { toggleWishlistItem } = useWishlistActions(customerState, initialWishlist);

    const { productId } = product;

    const { openSnackbar } = useSnackbarContext();

    const isOnWishlist = useIsInWishlist(productId, initialWishlist);

    const toggleEventType = isOnWishlist
        ? WishListEventType.RemoveFromWishlist
        : WishListEventType.AddToWishlist;

    const onWishlistButtonClick = async (): Promise<void> => {
        try {
            await toggleWishlistItem(productId);
            await window.yieldToMainThread();

            trackToggleWishlist({
                type: toggleEventType,
                productId,
                productTitle: product.title,
                itemListName,
            });

            if (!isOnWishlist) {
                trackEecToggleWishlistItem(
                    buildEecWishlistPayload(product, itemListName, hasLocationFilter),
                );
            }
        } catch (error) {
            if (isWishlistCountExceeded(error)) {
                openSnackbar({
                    variant: NotificationVariant.Error,
                    content: messages.tooManyWishlistArticlesError,
                    a11yTextCloseButton: messages.a11yCloseErrorMessage,
                });
                trackWishlistError(
                    WishlistError.MoreThan99Products,
                    messages.tooManyWishlistArticlesError,
                );
            } else {
                errorLogger(error as Error);
            }
        }
    };

    return { isOnWishlist, onWishlistButtonClick };
};

export { useWishListToggle };
